import { DateTime } from "luxon";
import React, { useState, useEffect, useContext, useCallback } from "react";
import ReactDatePicker from "react-datepicker";
import Select from "react-select";
import cn from "classnames";
import { useHistory, useParams } from "react-router";
import useAPI from "../Api/Api";
import SalesLayout from "./SalesLayout";
import { FilterContext } from "../FilterContextProvider";
import useAllProducts from "../hooks/useAllProducts";
import Input from "../Components/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinusCircle } from "@fortawesome/free-solid-svg-icons";
import Button from "../Components/Button";
import { NotificationManager } from "react-notifications";
import CleaveInput from "../Components/CleaveInput";
import WebSalesLayout from "./WebSalesLayout";
import Papa from "papaparse";

import "react-datepicker/dist/react-datepicker.css";
import CsvFileInput from "../Components/CsvFileInput";

const WebSales = () => {
  const api = useAPI();
  const { year, month } = useParams();
  const history = useHistory();

  const [reportDate, setReportDate] = useState(
    year && month
      ? DateTime.fromFormat(
          `${year}-${month.padStart(2, "0")}-01`,
          "yyyy-MM-dd"
        ).toJSDate()
      : DateTime.now().startOf("month").toJSDate()
  );
  const [event, setEvent] = useState(null);
  const {
    filterData: { artist_id },
  } = useContext(FilterContext);
  const { allProducts, fetchProducts } = useAllProducts(true);
  const [isFetching, setFetching] = useState(false);
  const [changedSales, setChangedSales] = useState({});

  const productOptions = allProducts.map((product) => {
    const { id, name, size, size_other } = product;
    return {
      label: `${name} ${size?.name ?? size_other ?? ""}`,
      value: id,
      product,
    };
  });

  const fetchEvent = useCallback(() => {
    api
      .get(
        `/events?start_date=${DateTime.fromJSDate(reportDate).toFormat(
          "yyyy-MM-dd"
        )}&end_date=${DateTime.fromJSDate(reportDate).toFormat(
          "yyyy-MM-dd"
        )}&reporting_type=web&artist_id=${artist_id}`
      )
      .then(({ data: { data } }) => {
        if (data.length > 1) {
          console.warn(
            `Multiple web events exist for ${DateTime.fromJSDate(
              reportDate
            ).toFormat("yyyy-MM-dd")} (artist ${artist_id})`,
            data
          );
        }
        return data.length
          ? data[0]
          : {
              artist_id,
              reporting_type: "web",
              name: DateTime.fromJSDate(reportDate).toFormat("MMMM yyyy"),
              date: DateTime.fromJSDate(reportDate).toFormat("yyyy-MM-dd"),
              sales: [],
            };
      })
      .then((event) => {
        if (!event.id) {
          return event;
        }
        return api
          .get(`/sales/all?artist_id=${artist_id}&event_id=${event.id}`)
          .then(({ data }) => ({
            ...event,
            sales: data.map((sale) => ({
              ...sale,
              total_sales: (sale.mail_order ?? 0) * (sale.price ?? 0),
            })),
          }));
      })
      .then((event) => {
        console.log("setEvent", event);
        setEvent(event);
      });
  }, [api, reportDate, artist_id]);

  useEffect(() => {
    fetchEvent();
  }, [reportDate, artist_id, fetchEvent]);

  const handleProductChange = (option, sale, i) => {
    console.log("handleProductChange", option, sale, i);

    setChangedSales({
      ...changedSales,
      [sale.id ?? i]: {
        ...sale,
        artist_id,
        product_id: option.value,
        product_name: option.product.name,
        product_category: option.product.category?.name,
        category_id: option.product.category_id,
        size_id: option.product.size?.id,
        product_size: option.product.size_other ?? option.product.size?.name,
        cost: option.product.cost,
        total_sales: sale.total_sales ?? 0,
        mail_order: sale.mail_order ?? 0,
      },
    });
  };

  const handleInputChange = (event, sale, i) => {
    console.log("handleInputChange", event, sale, i);
    setChangedSales({
      ...changedSales,
      [sale.id ?? i]: {
        ...sale,
        [event.target.name]: event.target.value,
      },
    });
  };

  console.log(changedSales);

  const removeSale = (sale, i) => {
    if (sale.id) {
      setChangedSales({
        ...changedSales,
        [sale.id]: { ...sale, discarded_at: new Date() },
      });
    } else {
      const updatedChangedSales = { ...changedSales };
      delete updatedChangedSales[i];
      setChangedSales(updatedChangedSales);
    }
  };

  const handleSubmit = () => {
    setFetching(true);
    const payload = {
      event: {
        ...event,
        gross_sales: calculateGrossSales(),
        sales_attributes: Object.values(changedSales).map((sale) => ({
          ...sale,
          price:
            parseFloat(sale.total_sales.replace(/,/g, "")) /
            parseInt(sale.mail_order),
          reporting_type: "web",
          date: event.date,
        })),
      },
    };
    (event.id
      ? api.put(`/events/${event.id}`, payload)
      : api.post(`/events`, payload)
    )
      .then((response) => {
        NotificationManager.success(
          "Web sales report updated",
          "Success",
          2000
        );

        return Promise.all([fetchEvent(), fetchProducts()]);
      })
      .then(() => {
        setChangedSales({});
      })
      .catch((error) => {
        NotificationManager.error("Web sales update failed", "Failure", 2000);
        console.error("WebSales", error);
      })
      .finally(() => setFetching(false));
  };

  const renderSaleRow = (sale, i) => {
    const saleToRender = changedSales[sale.id] ?? sale;
    if (!saleToRender.product_id && !saleToRender.sku) {
      return (
        <tr
          key={saleToRender.id ?? i}
          className="border-t text-xs h-11 text-center"
        >
          <td className="text-sm text-left pl-4 py-2" colSpan="2">
            <Select
              id={`product_id_${i}`}
              name="product_id"
              placeholder="Select a product"
              className="text-xs font-light"
              options={productOptions}
              value={productOptions.filter(
                ({ value }) => value === saleToRender.product_id
              )}
              onChange={(option) =>
                handleProductChange(option, saleToRender, i)
              }
              disabled={isFetching}
            />
          </td>
        </tr>
      );
    }
    return (
      <tr
        key={saleToRender.id ?? i}
        className={cn("border-t text-xs h-11 text-center", {
          "bg-yellow-50": changedSales[saleToRender.id ?? i],
        })}
      >
        <td className="text-sm text-left px-4">
          <Select
            id={`product_id_${i}`}
            name="product_id"
            placeholder="Select a product"
            className={cn("text-xs font-light", {
              error: !saleToRender.product_id,
            })}
            classNamePrefix="react-select"
            options={productOptions}
            value={productOptions.filter(
              ({ value }) => value === saleToRender.product_id
            )}
            onChange={(option) => handleProductChange(option, saleToRender, i)}
            disabled={isFetching}
          />
        </td>
        <td className="text-left">
          <Input
            id={`sku_${i}`}
            name="sku"
            onChange={(e) => handleInputChange(e, saleToRender, i)}
            value={saleToRender.sku ?? ""}
            disabled={isFetching}
          />
        </td>
        <td>
          <CleaveInput
            id={`mail_order_${i}`}
            name="mail_order"
            onChange={(e) => handleInputChange(e, saleToRender, i)}
            cleaveOptions={{ numeral: true, numeralPositiveOnly: true }}
            value={saleToRender.mail_order}
            inputClassName="w-20 mx-auto text-center"
            disabled={isFetching}
          />
        </td>
        <td>
          <CleaveInput
            id={`total_sales_${i}`}
            name="total_sales"
            cleaveOptions={{ numeral: true, numeralPositiveOnly: true }}
            onChange={(e) => handleInputChange(e, saleToRender, i)}
            value={saleToRender.total_sales}
            className="w-20 mx-auto"
            inputClassName="w-20"
            prefix={"$"}
            disabled={isFetching}
          />
        </td>
        <td>
          <button
            className="px-4 py-2 mx-4"
            onClick={(e) => {
              e.preventDefault();
              if (
                window.confirm("Are you sure you want to remove that sale?")
              ) {
                removeSale(saleToRender, i);
              }
            }}
            disabled={isFetching}
          >
            <FontAwesomeIcon icon={faMinusCircle} />
          </button>
        </td>
      </tr>
    );
  };

  const calculateGrossSales = () => {
    return [
      ...(event?.sales ?? [])
        .filter(({ id }) => !changedSales[id]?.discarded_at)
        .map((sale) => changedSales[sale.id] ?? sale),
      ...Object.keys(changedSales)
        .filter((key) => key.startsWith("new"))
        .map((key) => changedSales[key]),
    ]
      .map(({ total_sales }) => parseFloat(total_sales.toString().replace(/,/g, "") ?? 0))
      .map((amount) => (window.isFinite(amount) ? amount : 0))
      .reduce((a, e) => a + e, 0);
  };

  const parseCsv = (file) => {
    Papa.parse(file, {
      complete: (data) => {
        console.log("parse results", data);
        const rows = data.data;
        const QUANTITY = "Qty Sold";
        let headerRow = 0;
        while (
          headerRow < rows.length &&
          !rows[headerRow].filter((col) => col === QUANTITY).length
        ) {
          headerRow++;
        }
        if (headerRow >= rows.length) {
          console.warn("No header row found");
          return;
        }
        const objects = rows
          .slice(headerRow + 1)
          .map((row, i) => {
            let mappedObject = {
              sku: row[0],
              quantity: parseInt(row[6] ?? 0),
              total_sales: parseFloat(row[7]?.replace("$", "") ?? 0),
            };
            return mappedObject;
          })
          .filter(
            (mappedRow) =>
              mappedRow.sku &&
              mappedRow.sku !== "Totals" &&
              mappedRow.sku !== rows[headerRow - 1][0]
          )
          .map(({ sku, quantity, total_sales }) => {
            const product = allProducts.filter(
              (product) =>
                sku && product.sku?.toLowerCase() === sku.toLowerCase()
            )[0];
            return {
              artist_id,
              product_id: product?.id ?? "",
              product_name: product?.name ?? "",
              product_category: product?.category?.name ?? "",
              category_id: product?.category_id ?? "",
              size_id: product?.size?.id ?? "",
              product_size: product?.size_other ?? product?.size?.name ?? "",
              cost: product?.cost ?? "",
              sku,
              total_sales,
              mail_order: quantity,
            };
          });
        console.log(objects);
        let updatedChangedSales = { ...changedSales };
        objects.forEach((sale, i) => {
          updatedChangedSales = {
            ...updatedChangedSales,
            [`new_${file.name}_${i}`]: sale,
          };
        });
        setChangedSales(updatedChangedSales);
      },
    });
  };

  if (!artist_id) {
    return (
      <SalesLayout>
        <div className="text-black bg-gray-100 pt-2 pb-1">
          <div className="mt-8 mb-12 mx-8 pt-4 px-12 text-black bg-white rounded-lg border">
            <div className="m-8 py-12 pl-4">
              Please select an artist from the filter to view Web Sales
            </div>
          </div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout>
      <WebSalesLayout>
        <div className="flex justify-between">
          <div>
            <label htmlFor="report_date" className="text-xs font-light">
              Month
            </label>
            <ReactDatePicker
              name="report_date"
              selected={reportDate}
              onChange={(date) => {
                if (
                  !Object.keys(changedSales).length ||
                  window.confirm(
                    "You have unsaved changes. Are you sure you want to change months?"
                  )
                ) {
                  history.push(
                    `/sales/websales/view/${date.getFullYear()}/${
                      date.getMonth() + 1
                    }`
                  );
                  setReportDate(date);
                  setChangedSales({});
                }
              }}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              className="text-sm font-light border border-gray-300 rounded-md px-2 py-1 cursor-pointer"
            />
          </div>
          <div className="text-lg text-black mt-2">
            Gross Sales: ${calculateGrossSales()?.toFixed(2)}
          </div>
        </div>
        <div className="-mx-12 py-4">
          <form>
            <table className="w-full">
              <thead className="text-xs font-light">
                <tr className="border-b-2 h-11 text-center uppercase">
                  <td className="w-1/3 min-w-20">Product</td>
                  <td className="w-1/3 min-w-20">SKU</td>
                  <td className="min-w-20">Quantity Sold</td>
                  <td className="min-w-20">Gross Sales</td>
                  <td></td>
                </tr>
              </thead>
              <tbody>
                {(event?.sales ?? [])
                  .filter(({ id }) => !changedSales[id]?.discarded_at)
                  .map(renderSaleRow)}
                {Object.keys(changedSales)
                  .filter((key) => key.startsWith("new"))
                  .map((key) => renderSaleRow(changedSales[key], key))}
                {renderSaleRow(
                  {},
                  `new_${
                    (event?.sales?.length ?? 0) +
                    Object.keys(changedSales).length
                  }`
                )}
              </tbody>
            </table>
          </form>
          {!!Object.keys(changedSales).length && (
            <div className="flex justify-center py-4">
              <Button
                className="button button-white mr-2"
                onClick={() => setChangedSales({})}
                text="Revert"
                disabled={isFetching}
              ></Button>
              <Button
                className="button"
                onClick={handleSubmit}
                text={isFetching ? `Loading ${"\u00A0"}` : "Update"}
                disabled={isFetching}
                inProgress={isFetching}
              ></Button>
            </div>
          )}
          <div className="w-1/2 mx-auto">
            <CsvFileInput
              onFilesChanged={(fileList) => {
                console.log("onFilesChanged", fileList);
                fileList?.forEach((file) => {
                  parseCsv(file);
                });
              }}
            />
          </div>
        </div>
      </WebSalesLayout>
    </SalesLayout>
  );
};

export default WebSales;
